/**
 * 开发环境占位图指令
 * 
 * 用法示例:
 * 1. 基本用法 (使用默认占位图):
 *    <van-image v-dev-placeholder :src="imageUrl" />
 * 
 * 2. 指定占位图类型:
 *    <van-image v-dev-placeholder:banner :src="bannerUrl" />
 *    <van-image v-dev-placeholder:cover :src="coverUrl" />
 *    <van-image v-dev-placeholder:avatar :src="avatarUrl" />
 * 
 * 3. 自定义占位图:
 *    <van-image v-dev-placeholder="'https://custom.com/placeholder.jpg'" :src="imageUrl" />
 * 
 * 4. 普通 img 标签:
 *    <img v-dev-placeholder :src="imageUrl" />
 */

// 占位图配置
const PLACEHOLDER_CONFIG = {
  // 默认占位图 URL (可以使用在线占位图服务)
  default: 'https://via.placeholder.com/400x300/7e5678/ffffff?text=Loading',
  
  // 或者使用本地图片 (需要先在 assets 中准备图片)
  // default: require('@/assets/placeholder.png'),
  
  // 不同尺寸的占位图
  banner: 'http://iph.href.lu/620x300?text=Banner',
  cover: 'http://iph.href.lu/335x290?text=Cover',
  avatar: 'http://iph.href.lu/100x100?text=Avatar',
  game: 'http://iph.href.lu/100x100?text=Game',
  
  // 是否启用 (根据环境变量判断)
  enabled: process.env.NODE_ENV === 'development'
}

/**
 * 获取占位图 URL
 * @param {string|Object} binding - 指令绑定对象
 * @returns {string} 占位图 URL
 */
function getPlaceholder(binding) {
  // 如果传入了自定义占位图 URL
  if (binding.value && typeof binding.value === 'string') {
    return binding.value
  }
  
  // 根据参数获取预设的占位图类型
  const type = binding.arg || 'default'
  return PLACEHOLDER_CONFIG[type] || PLACEHOLDER_CONFIG.default
}

/**
 * 处理 van-image 组件
 * 通过修改实际的 img DOM 元素，而不是修改组件的 prop
 */
function handleVanImage(el, binding, vnode) {
  if (!PLACEHOLDER_CONFIG.enabled) {
    return
  }
  
  // 获取占位图 URL
  const placeholderUrl = getPlaceholder(binding)
  
  // 等待组件渲染完成，找到实际的 img 元素
  const setPlaceholder = () => {
    const imgElement = el.querySelector('img')
    if (imgElement) {
      // 保存原始 src (只在第一次保存)
      if (!el._originalSrc) {
        el._originalSrc = imgElement.getAttribute('src')
      }
      
      // 直接修改 img 元素的 src 属性
      imgElement.setAttribute('src', placeholderUrl)
      
      // 同时修改 srcset (如果存在)
      if (imgElement.hasAttribute('srcset')) {
        if (!el._originalSrcset) {
          el._originalSrcset = imgElement.getAttribute('srcset')
        }
        imgElement.setAttribute('srcset', placeholderUrl)
      }
    }
  }
  
  // 如果组件已经渲染，立即设置
  if (vnode.componentInstance) {
    vnode.componentInstance.$nextTick(() => {
      setPlaceholder()
    })
  } else {
    // 否则等待 DOM 更新
    setTimeout(setPlaceholder, 0)
  }
}

/**
 * 处理普通 img 标签
 */
function handleImgTag(el, binding) {
  if (!PLACEHOLDER_CONFIG.enabled) {
    return
  }
  
  // 保存原始 src
  if (!el._originalSrc) {
    el._originalSrc = el.getAttribute('src')
  }
  
  // 设置占位图
  const placeholderUrl = getPlaceholder(binding)
  el.setAttribute('src', placeholderUrl)
}

/**
 * 自定义指令定义
 */
export default {
  // 指令绑定到元素时调用
  bind(el, binding, vnode) {
    if (!PLACEHOLDER_CONFIG.enabled) {
      return
    }
    
    // 根据元素类型处理
    if (el.tagName === 'IMG') {
      handleImgTag(el, binding)
    } else {
      // 处理 van-image 组件
      handleVanImage(el, binding, vnode)
    }
  },
  
  // 组件插入 DOM 后调用
  inserted(el, binding, vnode) {
    if (!PLACEHOLDER_CONFIG.enabled) {
      return
    }
    
    if (el.tagName !== 'IMG') {
      // 确保组件完全渲染后再设置占位图
      handleVanImage(el, binding, vnode)
    }
  },
  
  // 组件更新时调用 (防止数据更新后恢复原图)
  update(el, binding, vnode) {
    if (!PLACEHOLDER_CONFIG.enabled) {
      return
    }
    
    if (el.tagName === 'IMG') {
      handleImgTag(el, binding)
    } else {
      // 延迟执行，确保 DOM 已更新
      setTimeout(() => {
        handleVanImage(el, binding, vnode)
      }, 0)
    }
  },
  
  // 指令与元素解绑时调用
  unbind(el) {
    delete el._originalSrc
    delete el._originalSrcset
  }
}

// 导出配置供外部使用
export { PLACEHOLDER_CONFIG, getPlaceholder }
